import React, { useCallback, useEffect, useRef, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { RecommendationBanner } from '../../../types/common';

export const RECOMMENDATION_BANNER_SLIDE_TYPE = 'recommendation-banner';

const BANNER_HEIGHT = 363;
const BANNER_AUTO_SCROLL_INTERVAL_MS = 3000;

type RecommendationBannerCarouselProps = {
  banners: RecommendationBanner[];
  onBannerPress: (banner: RecommendationBanner, index: number) => void;
  onBannerImpression: (banner: RecommendationBanner, index: number) => void;
  onBannerSlide: (banner: RecommendationBanner, index: number) => void;
  slideType?: typeof RECOMMENDATION_BANNER_SLIDE_TYPE;
};

const BannerContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  boxSizing: 'border-box',
  width: '100%',
  height: BANNER_HEIGHT,
  minWidth: 160,
  minHeight: 256.59,
  overflow: 'hidden',
  border: `1px solid ${theme.palette.grey[200]}`,
  borderRadius: theme.spacing(1),
  backgroundColor: theme.palette.common.white,
}));

const BannerViewport = styled(Box)(() => ({
  width: '100%',
  height: '100%',
  overflow: 'hidden',
}));

const BannerTrack = styled(Box)(() => ({
  display: 'flex',
  height: '100%',
}));

const BannerSlide = styled(Box)(() => ({
  position: 'relative',
  flex: '0 0 100%',
  minWidth: 0,
  height: '100%',
  cursor: 'pointer',
}));

const BannerImage = styled('img')(() => ({
  display: 'block',
  width: '100%',
  height: '100%',
  objectFit: 'cover',
}));

const BannerDots = styled(Box)(({ theme }) => ({
  position: 'absolute',
  left: 0,
  right: 0,
  bottom: theme.spacing(1.5),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(0.5),
  pointerEvents: 'auto',
}));

const BannerDot = styled('button', {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active?: boolean }>(({ active }) => ({
  width: 6,
  height: 6,
  padding: 0,
  border: 0,
  borderRadius: '50%',
  background: active ? '#FFFFFF' : 'rgba(255, 255, 255, 0.5)',
  cursor: 'pointer',
}));

const RecommendationBannerCarousel: React.FC<RecommendationBannerCarouselProps> = ({
  banners,
  onBannerPress,
  onBannerImpression,
  onBannerSlide,
}) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    loop: banners.length > 1,
  });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const selectedIndexRef = useRef(0);
  const isManualBannerScrollRef = useRef(false);

  useEffect(() => {
    if (!banners.length) return;

    selectedIndexRef.current = 0;
    setSelectedIndex(0);
    emblaApi?.scrollTo(0, true);
    onBannerImpression(banners[0], 0);
  }, [banners, emblaApi, onBannerImpression]);

  const handleSelect = useCallback(() => {
    if (!emblaApi) return;

    const index = emblaApi.selectedScrollSnap();
    if (index === selectedIndexRef.current) {
      isManualBannerScrollRef.current = false;
      return;
    }

    const banner = banners[index];
    const shouldTrackBannerSlide = isManualBannerScrollRef.current;
    selectedIndexRef.current = index;
    setSelectedIndex(index);

    if (!banner) {
      isManualBannerScrollRef.current = false;
      return;
    }

    onBannerImpression(banner, index);
    if (shouldTrackBannerSlide) {
      onBannerSlide(banner, index);
    }
    isManualBannerScrollRef.current = false;
  }, [banners, emblaApi, onBannerImpression, onBannerSlide]);

  useEffect(() => {
    if (!emblaApi) return;

    const markManualBannerScroll = () => {
      isManualBannerScrollRef.current = true;
    };

    emblaApi.on('pointerDown', markManualBannerScroll);
    emblaApi.on('select', handleSelect);

    return () => {
      emblaApi.off('pointerDown', markManualBannerScroll);
      emblaApi.off('select', handleSelect);
    };
  }, [emblaApi, handleSelect]);

  useEffect(() => {
    if (!emblaApi || banners.length <= 1) return;

    const intervalId = window.setInterval(() => {
      emblaApi.scrollNext();
    }, BANNER_AUTO_SCROLL_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [banners.length, emblaApi]);

  if (!banners.length) {
    return null;
  }

  return (
    <BannerContainer data-slide-type={RECOMMENDATION_BANNER_SLIDE_TYPE}>
      <BannerViewport ref={emblaRef}>
        <BannerTrack>
          {banners.map((banner, index) => (
            <BannerSlide
              key={`${banner.bannerUrl}-${index}`}
              onClick={() => onBannerPress(banner, index)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onBannerPress(banner, index);
                }
              }}
              role="button"
              tabIndex={0}
            >
              <BannerImage src={banner.bannerUrl} alt={banner.title ?? banner.name ?? ''} />
            </BannerSlide>
          ))}
        </BannerTrack>
      </BannerViewport>
      {banners.length > 1 && (
        <BannerDots>
          {banners.map((banner, index) => (
            <BannerDot
              key={`${banner.bannerUrl}-${index}`}
              active={index === selectedIndex}
              type="button"
              aria-label={`Show banner ${index + 1}`}
              onClick={(event) => {
                event.stopPropagation();
                isManualBannerScrollRef.current = true;
                emblaApi?.scrollTo(index);
              }}
            />
          ))}
        </BannerDots>
      )}
    </BannerContainer>
  );
};

export default RecommendationBannerCarousel;
